import { ObservableState } from '@mimir/common/util/observable';
import type { AudioManager } from './audioManager';
import { decodeAudio, downloadAll, getPeaks } from './audioUtils';
import { updateAudioTrackConfig, type AudioTrackConfig, type AudioTrackConfigUpdate, type PeakRange } from '@mimir/web/model/audioTrackConfig';
import type { PeaksManagerOps } from '@mimir/web/model/peaksManagerOps';
import type { AudioInitializationCallbacks } from '@mimir/web/model/itemMediaPlayerOps';


/**
 * The SeparateAudioProxiesAudioManager class can be used in the legacy
 * item details page player to play audio from the proxies, in which case,
 * we need to keep the audio context around as well as having information
 * about the default mixing.
 *
 * However, when we just need the peaks, we can load the audio
 * proxies, decode them and immediately close the audio context.
 */
export async function createPeaksManagerFromAudioProxies(
  channelUrls: string[],
  peakCount: ObservableState<number>,
  callbacks: AudioInitializationCallbacks,
): Promise<PeaksManagerOps> {
  // For peaks, we don't care the audio configuration
  const audioTrackConfigs = new ObservableState<AudioTrackConfig[]>([]);
  const emptyChannelSet = new Set<number>();

  const audioManager = new SeparateAudioProxiesAudioManager(audioTrackConfigs, emptyChannelSet, emptyChannelSet);
  await audioManager.downloadAndDecodeChannels(channelUrls, emptyChannelSet, emptyChannelSet, callbacks);
  audioManager.cleanup();
  peakCount.value = audioManager.getPeakCount();

  return {
    getPeakSeriesCount: (audioTrackIndex: number) => audioManager.getPeakSeriesCount(audioTrackIndex),
    getPeaks: (audioTrackIndex, channel, range, width) => audioManager.getPeaks(audioTrackIndex, channel, range, width),
    getPeakCount: () => peakCount.getObservable(),
  };
}


export async function createSeparateAudioProxiesAudioManager(
  audioTrackConfigs: ObservableState<AudioTrackConfig[]>,
  channelUrls: string[],
  defaultLeftChannels: Set<number>,
  defaultRigthChannels: Set<number>,
  callbacks: AudioInitializationCallbacks,
): Promise<AudioManager> {
  const audioManager = new SeparateAudioProxiesAudioManager(audioTrackConfigs, defaultLeftChannels, defaultRigthChannels);
  await audioManager.downloadAndDecodeChannels(channelUrls, defaultLeftChannels, defaultRigthChannels, callbacks);
  return audioManager;
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: webkitAudioContext is a browser legacy property that is not found in node
const AudioContext = window.AudioContext || window.webkitAudioContext;

interface PlayoutSource {
  isPlaying: boolean;
  source: AudioBufferSourceNode;
}

class SeparateAudioProxiesAudioManager implements AudioManager {
  // audioBuffers[i] === null means that the audio proxy #i does not exist
  private audioBuffers: (AudioBuffer | null)[] = [];

  private audioContext: AudioContext;
  private gainNode: GainNode;
  private playoutSources: PlayoutSource[] = [];
  private _playbackRate: number = 1;
  private leftChannels: Set<number> =  new Set();
  private rightChannels: Set<number> =  new Set();

  constructor(private audioTrackConfigs: ObservableState<AudioTrackConfig[]>, leftChannels: Set<number>, rightChannels: Set<number>) {
    this.audioContext = new AudioContext();
    this.gainNode = this.audioContext.createGain();
    this.gainNode.connect(this.audioContext.destination);
    this.leftChannels = leftChannels;
    this.rightChannels = rightChannels;
  }

  setPlaybackRate(playbackRate: number) {
    this._playbackRate = playbackRate;
    this.stopAll();
    this.schedulePlayout();
  }

  stopAll() {
    if (this.audioContext.state === 'running') {
      this.playoutSources.forEach(sourceInfo => {
        if (sourceInfo.isPlaying) {
          sourceInfo.source.stop();
          sourceInfo.source.disconnect();
        }
      });
    }
    this.playoutSources = [];
  }

  hasScheduledSources(): boolean {
    return this.playoutSources.length > 0;
  }

  playScheduled(videoCurrentTimeInMs: number) {
    const when = this.audioContext.currentTime;
    this.playoutSources.forEach(sourceInfo => {
      if (!sourceInfo.isPlaying) {
        sourceInfo.source.playbackRate.value = this._playbackRate;
        sourceInfo.isPlaying = true;
        sourceInfo.source.start(when, videoCurrentTimeInMs / 1000);
      }
    });
  }

  getChannelsAsMatrix(): number[][] {
    const matrix: number[][] = [];

    this.audioTrackConfigs.value.forEach((config, i) => {
      if (!config.isMuted) {
        // Create stereo pair [leftGain, rightGain]
        let isLeft = this.leftChannels.has(i);
        let isRight = this.rightChannels.has(i);

        if (!isLeft && !isRight) {
          isLeft = i % 2 === 0;
          isRight = !isLeft;
        }

        const stereoGains = [
          isLeft ? 1 : 0,  // left channel gain
          isRight ? 1 : 0  // right channel gain
        ];
        matrix.push(stereoGains);
      } else {
        matrix.push([]);  // muted track
      }
    });
    return matrix;
  }

  setVolume(volume: number) {
    this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
  }

  schedulePlayout() {
    if (this.playoutSources.length) {
      this.stopAll();
    }
    this.playoutSources = [];
    for (let i = 0; i < this.audioTrackConfigs.value.length; i++) {
      const channel = this.audioTrackConfigs.value[i];
      if (!channel.isMuted) {
        const source = this.audioContext.createBufferSource();
        source.buffer = this.audioBuffers[i];

        let isLeft = this.leftChannels.has(i);
        let isRight = this.rightChannels.has(i);

        // If no channel is specified, even channels are left and odd channels are right
        if (!isLeft && !isRight) {
          isLeft = i % 2 === 0;
          isRight = !isLeft;
        }

        const panner = this.audioContext.createStereoPanner();

        if (isLeft && isRight) {
          panner.pan.value = 0;  // Center
        } else if (isLeft) {
          panner.pan.value = -1; // Full left
        } else if (isRight) {
          panner.pan.value = 1;  // Full right
        }

        source.connect(panner);
        panner.connect(this.gainNode);

        source.onended = () => {
          source.disconnect();
        };

        this.playoutSources.push({
          source: source,
          isPlaying: false,
        });
      }
    }
  }

  async downloadAndDecodeChannels(
    channelUrls: string[],
    defaultLeftChannels: Set<number>,
    defaultRightChannels: Set<number>,
    callbacks: AudioInitializationCallbacks,
  ) {
    try {
      const audioProxiesFileContent: (ArrayBuffer | null)[] =
        await downloadAll(channelUrls, progress => callbacks.onDowloadProgress(progress));

      if (audioProxiesFileContent.every(v => v === null)) {
        throw 'No audio proxies at all';
      }
      callbacks.onStartDecoding();
      const decodedAudioBuffers: (AudioBuffer | null)[] = await Promise.all(
        audioProxiesFileContent.map((audioProxyFileContent) => {
          return audioProxyFileContent === null ? null : decodeAudio(this.audioContext, audioProxyFileContent);
        })
      );

      this.audioBuffers = decodedAudioBuffers;
      const configs: AudioTrackConfig[] = [];

      const hasDefaultConfig = defaultLeftChannels.size > 0 || defaultRightChannels.size > 0;
      for (let i = 0; i < decodedAudioBuffers.length; i++) {
        const shouldPlay = !hasDefaultConfig || defaultLeftChannels.has(i) || defaultRightChannels.has(i);
        configs.push({ isMuted: !shouldPlay });
      }
      this.audioTrackConfigs.value = configs;
      callbacks.showPeaks();
    } catch (err) {
      callbacks.onError(err);
    }
  }

  updateAudioTrackConfig(update: AudioTrackConfigUpdate[]): void {
    this.audioTrackConfigs.value =
      updateAudioTrackConfig(this.audioTrackConfigs.value, update);
  }

  /**
   * Free resources asynchronously in the background.
   *
   * Calling any other AudioManager on functions after calling cleanup() is not
   * permitted.
   */
  cleanup() {
    this.stopAll();
    this.audioContext.close().catch(err => console.log(err));
  }

  getPeakSeriesCount(streamIndex: number): number {
    return this.audioBuffers[streamIndex]?.numberOfChannels ?? 0;

  }

  getPeaks(streamIndex: number, peakSeriesIndex: number, range: PeakRange, fullWidth: number): number[] {
    const buffer = this.audioBuffers[streamIndex];
    if (!buffer) {
      return [];
    }
    return getPeaks(buffer.getChannelData(peakSeriesIndex), buffer.sampleRate, range, fullWidth);
  }

  getPeakCount(): number {
    const buffer = this.audioBuffers[0];
    if (!buffer) {
      return 0;
    }
    return buffer.getChannelData(0).length;
  }
}

